<template>
  <div v-if="materialAlterCodes" class="h-full">
    <BaseTable
      :headers="materialAlterCodesHeaders"
      :list="filteredMaterialAlterCodes"
      :is-add-new-hidden="mode == 'view'"
      table-id="materialAlterCodes"
      :total-records="materialAlterCodesCount"
      is-download-button-hidden
      :hideMultiselectDelete="mode == 'edit' ? false : true"
      hide-grouping
      @refresh-data="onRefreshData"
      @add-new="onOpenModal"
      @tableItemClicked="onOpenModal"
      :clickable="true"
      @deleteMultiselect="onDeleteMultiselect">
      <template #actions="{ item }" v-if="mode == 'edit'">
        <div class="flex items-center justify-center gap-1">
          <button
            v-if="item.isFromManager"
            class="rounded-full p-1.5 text-red-400 hover:bg-red-50"
            type="button"
            @click="onDeleteRow(item)">
            <IconTrashOutline class="size-4" />
          </button>
        </div>
      </template>
    </BaseTable>

    <MaterialDetailsCompanyAlterCodesModal
      :material-alter-code="companyAlterCode"
      :is-open="isModalOpen"
      :is-new="isNewAlterCode"
      :mode="modalMode"
      :sequence-list="sequenceList"
      @close="isModalOpen = false"
      @save="onSave" />

    <BaseModalConfirm
      v-if="modalDeleteRow"
      @cancel="modalDeleteRow = false"
      @confirm="onDeleteSingle()" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { getMaterialAlterCodesWithConditions } from '@/services/api/materials';
import { v4 as uuidv4 } from 'uuid';
import BaseTable from '../common/BaseTable.vue';
import BaseModalConfirm from '../common/BaseModalConfirm.vue';
import MaterialDetailsCompanyAlterCodesModal from './MaterialDetailsCompanyAlterCodesModal.vue';
import IconTrashOutline from '@/assets/icons/IconTrashOutline.vue';
import { eAlterCodeType, type MaterialAlterCode } from '@/types/Materials';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { storeToRefs } from 'pinia';
import { useLookupsStore } from '@/stores/lookups';
import { useI18n } from 'vue-i18n';

const { alterCodeTypes } = storeToRefs(useLookupsStore());

const props = defineProps<{
  mode: 'view' | 'edit';
  materialId: string;
  resetState: boolean;
  modifiedData: MaterialAlterCode[] | undefined;
}>();

const emit = defineEmits(['saveMaterialAlterCode']);

const materialAlterCodes = ref<MaterialAlterCode[]>([]);
const materialAlterCodesCount = ref<number>(0);

const filteredMaterialAlterCodes = computed(() => {
  const alterCodeTypeCompany = alterCodeTypes.value.find(
    (type) => type.eType == eAlterCodeType.CompanyType
  );
  return materialAlterCodes.value.filter(
    (x) => x.alterCode.alterCodeTypeId == alterCodeTypeCompany?.id
  );
});

const { t: $t } = useI18n({ useScope: 'global' });
const materialAlterCodesHeaders = ref([
  { value: 'alterCode.name', text: $t('baseObject.name'), type: 'text', active: true },
  { value: 'alterCode.code', text: $t('baseObject.code'), type: 'text', active: true },
  {
    value: 'measurementUnit.name',
    text: $t('material.measurementUnit'),
    type: 'text',
    active: true,
  },
  { value: 'relationFactor', text: $t('material.relationFactor'), type: 'number', active: true },
  { value: 'sequence', text: $t('baseObject.sequence'), type: 'number', active: true },
  { value: 'isDefault', text: $t('material.default'), type: 'check', active: true },
  { value: 'isActive', text: $t('baseObject.isActive'), type: 'status', active: true },
  { value: 'isFromManager', text: $t('baseObject.isFromManager'), type: 'check', active: true },
  { value: 'actions', text: $t('baseObject.actions'), active: true },
]);

function matchesQuery(item: MaterialAlterCode, query: string | undefined): boolean {
  if (!query) return true;
  return (
    item.boid?.toLowerCase().includes(query.toLowerCase()) ||
    item.alterCode.name.toLowerCase().includes(query.toLowerCase()) ||
    item.alterCode.code.toLowerCase().includes(query.toLowerCase()) ||
    item.measurementUnit.name.toLowerCase().includes(query.toLowerCase())
  );
}

async function onRefreshData(data: TableFilterData) {
  const page = data.page ?? 1;
  const itemsPerPage = data.itemsPerPage ?? 50;

  const propsData = props.modifiedData || [];
  const dataAdded = propsData.filter((m) => m.recordMode === RecordMode.Added);
  const dataModified = propsData.filter((m) => m.recordMode === RecordMode.Modified);
  const dataDeleted = propsData.filter((m) => m.recordMode === RecordMode.Deleted);

  // If there are search results in new items
  const existingDataWithQuery = dataAdded.filter((item) => matchesQuery(item, data.query));

  let itemsToFetch = itemsPerPage;
  if (page === 1) {
    itemsToFetch = Math.max(0, itemsPerPage);
  }

  const sortColumn =
    data.column !== undefined ? materialAlterCodesHeaders.value[data.column]?.value : '';
  const sortAscending = data.order === 'asc' || data.order === undefined;

  const params = {
    page: page,
    itemsPerPage: itemsToFetch,
    query: data.query || '',
    sortColumn,
    sortAscending,
    materialId: props.materialId,
    pricingPolicyId: null,
  };

  const res = await getMaterialAlterCodesWithConditions(params);

  if (!res) {
    materialAlterCodes.value = [];
    materialAlterCodesCount.value = 0;
    return;
  }

  let result = res.data;
  // Remove deleted
  result = result.filter((item) => !dataDeleted.some((delItem) => delItem.id === item.id));
  // Update modified
  result = result.map((item) => {
    const modifiedItem = dataModified.find((x) => x.id === item.id);
    return modifiedItem ? modifiedItem : item;
  });
  // Add new items in page 1
  if (page === 1 && existingDataWithQuery.length) {
    result = [...existingDataWithQuery, ...result];
  }

  materialAlterCodes.value = result;

  materialAlterCodesCount.value = res.count;
}

const isModalOpen = ref(false);
const companyAlterCode = ref<MaterialAlterCode>(initializeMaterialAlterCode());
const isNewAlterCode = ref<boolean>(false);
const sequenceList = computed<number[]>(() => {
  return filteredMaterialAlterCodes.value
    .map((x) => x.sequence)
    .filter((n): n is number => n !== null && n !== undefined);
});

function initializeMaterialAlterCode(): MaterialAlterCode {
  const alterCodeId = uuidv4();
  const alterCodeTypeCompany = alterCodeTypes.value.find(
    (type) => type.eType == eAlterCodeType.CompanyType
  );
  return {
    id: uuidv4(),
    isActive: true,
    isFromManager: true,
    boid: '',
    alterCodeId,
    alterCode: {
      id: alterCodeId,
      code: '',
      name: '',
      alterCodeTypeId: alterCodeTypeCompany?.id || '',
      recordMode: RecordMode.Added,
      isActive: true,
    },
    measurementUnitId: '',
    measurementUnit: { id: '', code: '', name: '' },
    recordMode: RecordMode.Added,
  };
}

const modalMode = ref<'view' | 'edit'>('view');
function onOpenModal(item: MaterialAlterCode) {
  if (item) {
    isNewAlterCode.value = false;
    companyAlterCode.value = item;
    companyAlterCode.value.recordMode = RecordMode.Modified;
    if (companyAlterCode.value.isFromManager && props.mode == 'edit') modalMode.value = 'edit';
    else modalMode.value = 'view';
  } else {
    isNewAlterCode.value = true;
    companyAlterCode.value = initializeMaterialAlterCode();
    modalMode.value = 'edit';
  }
  isModalOpen.value = true;
}

function onSave(alterCode: MaterialAlterCode) {
  const index = materialAlterCodes.value.findIndex((item) => item.id == alterCode.id);
  const itemsToSave: MaterialAlterCode[] = [alterCode];

  // Handle isDefault logic: if this item is being set as default,
  // ensure all other items are not default
  if (alterCode.isDefault === true && materialAlterCodes.value) {
    materialAlterCodes.value.forEach((item) => {
      if (item.id !== alterCode.id && item.isDefault === true) {
        item.isDefault = false;
        itemsToSave.push(item);
      }
    });
  }

  if (alterCode.recordMode === RecordMode.Deleted) {
    if (index !== -1 && index !== undefined && materialAlterCodes.value) {
      materialAlterCodes.value.splice(index, 1);
    } else {
      if (materialAlterCodes.value) materialAlterCodes.value.push(alterCode);
    }
  } else {
    if (index !== -1 && index !== undefined && materialAlterCodes.value) {
      materialAlterCodes.value[index] = alterCode;
    } else {
      if (materialAlterCodes.value) materialAlterCodes.value.push(alterCode);
    }
  }

  emit('saveMaterialAlterCode', itemsToSave);
  isModalOpen.value = false;
  isNewAlterCode.value = false;
}

const modalDeleteRow = ref(false);
const deleteRowItem = ref<MaterialAlterCode>();

function onDeleteRow(item: MaterialAlterCode) {
  deleteRowItem.value = item;
  modalDeleteRow.value = true;
}

function onDeleteSingle() {
  if (!deleteRowItem.value) return;
  deleteRowItem.value.recordMode = RecordMode.Deleted;
  modalDeleteRow.value = false;
  onSave(deleteRowItem.value);
}

function onDeleteMultiselect(items: MaterialAlterCode[]) {
  items.forEach((item) => {
    if (item.isFromManager) {
      item.recordMode = RecordMode.Deleted;
    }
    onSave(item);
  });
}

watch(
  () => props.resetState,
  async (newData) => {
    if (newData) {
      materialAlterCodes.value = [];
      await onRefreshData({
        page: 1,
        itemsPerPage: 50,
      });
    }
  }
);

onMounted(async () => {
  await onRefreshData({
    page: 1,
    itemsPerPage: 50,
  });
});
</script>
